import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import IconButton from 'components/common/IconButton';
import Menu, { MenuItem } from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconMoreVert from 'components/icons/IconMoreVert';
import SalesRepresentativeCreateEditDialog from 'components/settings/SalesRepresentativeCreateEditDialog';
import Link from 'components/common/Link';
import Typography from '@material-ui/core/Typography';
import Tooltip from 'components/common/Tooltip';
import IconLink from 'components/icons/IconLink';

import splitLongWords from 'utils/splitLongWords';

import { deleteSalesRepresentative } from 'redux/modules/salesRepresentatives';

const styles = theme => ({
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  buttonCell: {
    textAlign: 'right',
    padding: '0 10px 0 0',
    '&:last-child': {
      padding: '0 10px 0 0',
    },
  },
  cellPhone: {
    whiteSpace: 'nowrap',
  },
  iconLink: {
    color: theme.palette.primary[500],
  },
  tooltipList: {
    textAlign: 'left',
  },
});

class SalesRepresentativesTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      deleteMenuOpen: false,
      editDialogOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onEditMenuItemClick = this.onEditMenuItemClick.bind(this);
    this.onEditDialogClose = this.onEditDialogClose.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: false,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onEditMenuItemClick() {
    this.setState({
      anchorEl: null,
      editDialogOpen: true,
    });
  }

  onDeleteMenuItemClick() {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { ID, actions } = this.props;
    actions.deleteSalesRepresentative({ ID });
    this.setState({
      deleteMenuOpen: false,
      anchorEl: null,
    });
  }

  onEditDialogClose() {
    this.setState({ editDialogOpen: false });
  }

  render() {
    const { account, classes } = this.props;
    const { anchorEl, deleteMenuOpen, editDialogOpen } = this.state;
    const open = Boolean(anchorEl);

    return (
      <TableRow>
        <TableCell>
          <Link
            onClick={this.onEditMenuItemClick}
            breakWord
          >
            {splitLongWords(`${this.props['First Name']} ${this.props['Middle Name'] || ''} ${this.props['Last Name']}`)}
          </Link>
        </TableCell>
        <TableCell className={classes.cellPhone}>
          {this.props.Phone}
        </TableCell>
        <TableCell>
          {this.props.Email &&
          splitLongWords(this.props.Email, 40)}
        </TableCell>
        <TableCell className={classes.linkCell}>
          {(this.props['Referral Source'] && this.props['Referral Source'].length > 0) &&
            <Tooltip
              content={
                <div className={classes.tooltipList}>
                  <Typography variant="caption" gutterBottom>
                    Linked Referral Source:
                  </Typography>
                  {this.props['Referral Source'].map(item => (
                    <Typography key={item.ID}>
                      {item['Name of source']}
                    </Typography>
                  ))}
                </div>
              }
              placement="top"
            >
              <IconLink className={classes.iconLink} />
            </Tooltip>
          }
        </TableCell>
        <TableCell className={classes.buttonCell}>
          <IconButton
            aria-label="More"
            aria-owns={open ? `${this.props.ID}-sales-representative-menu` : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert className={classes.iconMoreVert} />
          </IconButton>
          <Menu
            id={`${this.props.ID}-sales-representative-menu`}
            anchorEl={anchorEl}
            open={open}
            onClose={this.onMenuClose}
            list={!deleteMenuOpen}
            maxHeight={130}
          >
            {deleteMenuOpen ? (
              <DeleteMenu
                text="Are you sure you want to delete this sales?"
                onCancel={this.onDeleteCancel}
                onSubmit={this.onDeleteSubmit}
              />
            ) : (
              <Fragment>
                <MenuItem
                  onClick={this.onEditMenuItemClick}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={this.onDeleteMenuItemClick}
                  color="warning"
                  disabled={account.Roles.filter(item =>
                    item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                >
                  Delete
                </MenuItem>
              </Fragment>
            )}
          </Menu>
        </TableCell>
        <SalesRepresentativeCreateEditDialog
          form={`salesRepresentativeEdit-${this.props.ID}`}
          open={editDialogOpen}
          ID={this.props.ID}
          initialValues={{
            'First Name': this.props['First Name'],
            'Last Name': this.props['Last Name'],
            ...this.props['Middle Name'] ? { 'Middle Name': this.props['Middle Name'] } : {},
            Phone: this.props.Phone,
            ...this.props.Email ? { Email: this.props.Email } : {},
            'Referral Source': this.props['Referral Source'].map(item => ({
              value: item.ID,
              label: item['Name of source'],
            })),
          }}
          onClose={this.onEditDialogClose}
        />
      </TableRow>
    );
  }
}

SalesRepresentativesTableRow.propTypes = {
  ID: PropTypes.number.isRequired,
  'First Name': PropTypes.string.isRequired,
  'Last Name': PropTypes.string.isRequired,
  'Middle Name': PropTypes.string,
  Phone: PropTypes.string.isRequired,
  Email: PropTypes.string,
  actions: PropTypes.shape({
    deleteSalesRepresentative: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    iconMoreVert: PropTypes.string.isRequired,
    buttonCell: PropTypes.string.isRequired,
    cellPhone: PropTypes.string.isRequired,
  }).isRequired,
};

SalesRepresentativesTableRow.defaultProps = {
  'Middle Name': null,
  Email: null,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ deleteSalesRepresentative }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(SalesRepresentativesTableRow),
);



// WEBPACK FOOTER //
// ./src/components/settings/SalesRepresentativesTable/SalesRepresentativesTableRow.js